import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { customerAPI, importAPI } from '../services/api';
import { Search, Plus, Eye, Edit2, Phone, MapPin, Upload, Download, X } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_COLORS = { active: 'success', suspended: 'warning', disconnected: 'danger', pending: 'info' };
const TYPE_COLORS = { broadband: 'broadband', catv: 'catv', both: 'both' };
const ISP_STYLES = {
  ISHAN:    { background: '#dbeafe', color: '#1d4ed8', label: 'Ishan' },
  CHAROTAR: { background: '#d1fae5', color: '#065f46', label: 'Charotar' },
};

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', status: '', customerType: '', area: '', ispName: '', page: 1, limit: 50 });
  const [areas, setAreas] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await customerAPI.getAll(params);
      setCustomers(data.data);
      setPagination(data.pagination);
    } catch {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    customerAPI.getAreas().then(({ data }) => setAreas(data.data)).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    setFilters(f => ({ ...f, search: e.target.value, page: 1 }));
  };

  const expiryDays = (date) => {
    if (!date) return null;
    const diff = Math.ceil((new Date(date) - new Date()) / 86400000);
    return diff;
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const fd = new FormData();
    fd.append('file', file);
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await importAPI.uploadCustomers(fd);
      setImportResult(data.data);
      if (data.data.success > 0) {
        toast.success(`${data.data.success} customers imported`);
        fetchCustomers();
      }
      if (data.data.failed > 0) toast.error(`${data.data.failed} rows failed — see details below`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const expiryBadge = (date) => {
    const days = expiryDays(date);
    if (days === null) return null;
    if (days < 0) return <span className="badge badge-danger">Expired</span>;
    if (days === 0) return <span className="badge badge-danger">Today</span>;
    if (days <= 3) return <span className="badge badge-warning">{days}d left</span>;
    if (days <= 7) return <span className="badge badge-info">{days}d left</span>;
    return null;
  };

  return (
    <div>
      {/* Hidden file input for import */}
      <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />

      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Customers</h2>
          <p className="text-muted text-small">{pagination.total || 0} total customers</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href={importAPI.getTemplateUrl()} className="btn btn-secondary" style={{ textDecoration: 'none' }}>
            <Download size={14} /> Template
          </a>
          <button className="btn btn-secondary" onClick={() => fileInputRef.current?.click()} disabled={importing}>
            <Upload size={14} /> {importing ? 'Importing...' : 'Import Excel'}
          </button>
          <Link to="/customers/add" className="btn btn-primary">
            <Plus size={15} /> Add Customer
          </Link>
        </div>
      </div>

      {/* Import result */}
      {importResult && (
        <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 8, background: importResult.failed > 0 ? '#fef3c7' : '#d1fae5', border: `1px solid ${importResult.failed > 0 ? '#f59e0b' : '#10b981'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Import complete: {importResult.success} created, {importResult.failed} failed
              </div>
              {importResult.errors?.slice(0, 5).map((e, i) => (
                <div key={i} style={{ fontSize: '.8rem', color: '#b45309' }}>Row {e.row}: {e.error}</div>
              ))}
              {importResult.errors?.length > 5 && (
                <div style={{ fontSize: '.8rem', color: '#b45309' }}>...and {importResult.errors.length - 5} more errors</div>
              )}
            </div>
            <button onClick={() => setImportResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: .6 }}><X size={16} /></button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={14} className="search-icon" />
          <input
            className="search-input" placeholder="Search name, mobile, ID..."
            value={filters.search} onChange={handleSearch}
          />
        </div>
        <select className="form-control" style={{ width: 130 }} value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="disconnected">Disconnected</option>
          <option value="pending">Pending</option>
        </select>
        <select className="form-control" style={{ width: 130 }} value={filters.customerType}
          onChange={e => setFilters(f => ({ ...f, customerType: e.target.value, page: 1 }))}>
          <option value="">All Types</option>
          <option value="broadband">Broadband</option>
          <option value="catv">CATV</option>
          <option value="both">Both</option>
        </select>
        <select className="form-control" style={{ width: 150 }} value={filters.area}
          onChange={e => setFilters(f => ({ ...f, area: e.target.value, page: 1 }))}>
          <option value="">All Areas</option>
          {areas.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="form-control" style={{ width: 120 }} value={filters.ispName}
          onChange={e => setFilters(f => ({ ...f, ispName: e.target.value, page: 1 }))}>
          <option value="">All ISPs</option>
          <option value="ISHAN">Ishan</option>
          <option value="CHAROTAR">Charotar</option>
        </select>
        <select className="form-control" style={{ width: 150 }} value={filters.expiringIn || ''}
          onChange={e => setFilters(f => ({ ...f, expiringIn: e.target.value, page: 1 }))}>
          <option value="">All Expiry</option>
          <option value="0">Expiring Today</option>
          <option value="3">Expiring in 3 Days</option>
          <option value="7">Expiring in 7 Days</option>
        </select>
        <select className="form-control" style={{ width: 100 }} value={filters.limit}
          onChange={e => setFilters(f => ({ ...f, limit: Number(e.target.value), page: 1 }))}>
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
          <option value={500}>All</option>
        </select>
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : customers.length === 0 ? (
            <div className="empty-state">
              <Search size={32} />
              <h3>No customers found</h3>
              <p>Try changing your filters or add a new customer.</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Area</th>
                  <th>Broadband</th>
                  <th>CATV</th>
                  <th>Balance</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c._id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>
                        <span className="font-mono">{c.customerId}</span>
                      </div>
                      <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Phone size={10} /> {c.mobile}
                      </div>
                    </td>
                    <td><span className={`badge badge-${TYPE_COLORS[c.customerType]}`} style={{ textTransform: 'capitalize' }}>{c.customerType}</span></td>
                    <td><span className={`badge badge-${STATUS_COLORS[c.status]}`} style={{ textTransform: 'capitalize' }}>{c.status}</span></td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.82rem' }}>
                        <MapPin size={11} color="var(--text-muted)" />{c.address?.area}
                      </div>
                    </td>
                    <td>
                      {c.broadband?.ispName && ISP_STYLES[c.broadband.ispName] && (
                        <span style={{ ...ISP_STYLES[c.broadband.ispName], fontSize: '.7rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, display: 'inline-block', marginBottom: 3 }}>
                          {ISP_STYLES[c.broadband.ispName].label}
                        </span>
                      )}
                      <div style={{ fontSize: '.8rem' }}>{c.broadband?.plan?.name || (c.customerType === 'broadband' ? '—' : '')}</div>
                      {expiryBadge(c.broadband?.expiryDate)}
                    </td>
                    <td>
                      <div style={{ fontSize: '.8rem' }}>{c.catv?.package?.name || '—'}</div>
                      {expiryBadge(c.catv?.expiryDate)}
                    </td>
                    <td>
                      {c.outstandingBalance > 0 ? (
                        <span style={{ color: 'var(--danger)', fontWeight: 600, fontSize: '.85rem' }}>
                          ₹{c.outstandingBalance.toLocaleString()}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--success)', fontSize: '.82rem' }}>Clear</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/customers/${c._id}`)}>
                          <Eye size={13} /> View
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={() => navigate(`/customers/${c._id}?edit=1`)}>
                          <Edit2 size={13} /> Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="pagination">
            <span style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginRight: 8 }}>
              Page {pagination.page} of {pagination.pages}
            </span>
            <button className="page-btn" disabled={!pagination.hasPrev}
              onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <button className="page-btn" disabled={!pagination.hasNext}
              onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}
      </div>
    </div>
  );
}
